import { z } from 'zod';
import { ReplenishmentEventTypeSchema } from '@erp/rpc';
import {
  demandDetailSearchSchema,
  type DemandDetailSearch,
} from '../forecast/demand/demandFilters';

/**
 * Where the page sits and how it is shown. It extends the forecast grid's
 * detail search, so the list the planner was walking (filters, sort, row
 * index, run) rides along and the record pager steps through the same list.
 */
export const replenishmentDetailSearchSchema = demandDetailSearchSchema.extend({
  mode: z
    .enum(['events', 'daily', 'weekly', 'monthly'])
    .optional()
    .catch(undefined),
  /** Show buckets with no supply or demand. Ignored in event mode. */
  empty: z.boolean().optional().catch(undefined),
  /** Visible event types. Absent shows every type. */
  types: z.array(ReplenishmentEventTypeSchema).optional().catch(undefined),
});

export type ReplenishmentDetailSearch = z.infer<
  typeof replenishmentDetailSearchSchema
>;

export type ReplenishmentDisplayMode = NonNullable<
  ReplenishmentDetailSearch['mode']
>;

/**
 * Planners read the plan a month at a time, with only the months that have
 * something in them (Empty buckets off).
 */
export const DEFAULT_DISPLAY_MODE: ReplenishmentDisplayMode = 'monthly';

type DisplayState = Pick<ReplenishmentDetailSearch, 'mode' | 'empty' | 'types'>;

/** How the page is shown, apart from where it sits. */
export function displayStateOf(
  search: ReplenishmentDetailSearch,
): DisplayState {
  return { mode: search.mode, empty: search.empty, types: search.types };
}

/** A forecast-list search (a pager target) re-shown with this page's display state. */
export function withDisplayState(
  target: DemandDetailSearch,
  current: ReplenishmentDetailSearch,
): ReplenishmentDetailSearch {
  return { ...target, ...displayStateOf(current) };
}

/**
 * A different item or location: the old row index and frozen run describe a
 * list position that no longer applies, so they go. Everything else stays.
 */
export function withRecord(
  search: ReplenishmentDetailSearch,
  record: { sku?: string; warehouseId?: string },
): ReplenishmentDetailSearch {
  return {
    ...search,
    ...record,
    rowIndex: undefined,
    navigationRunId: undefined,
  };
}

/** The Forecast Detail search for this item: the list's, minus display state. */
export function forecastSearchOf(
  search: ReplenishmentDetailSearch,
  forecastRunId: string | null | undefined,
): DemandDetailSearch {
  const { mode: _mode, empty: _empty, types: _types, ...forecast } = search;
  return {
    ...forecast,
    navigationRunId: forecastRunId ?? forecast.navigationRunId,
  };
}
